import {
        AlertTriangle,
        Archive,
        CheckCircle2,
        ChevronDown,
        CircleDashed,
        ListFilter,
        Loader2,
        Search,
        Star,
        Trash2,
        X,
} from "lucide-react";
import { ADMIN } from "@/constants/testIds";
import {
        activeWarningDetails,
        technicalWarningDetails,
} from "@/lib/editorialWarnings";

const EDITORIAL_STATUS_META = {
        published: {
                label: "Publicado",
                description: "visível no site",
                Icon: CheckCircle2,
                color: "var(--truth)",
                border: "rgba(46, 125, 50, 0.55)",
                background: "rgba(46, 125, 50, 0.11)",
        },
        review: {
                label: "Em revisão",
                description: "precisa de validação",
                Icon: AlertTriangle,
                color: "var(--ai)",
                border: "rgba(29, 78, 216, 0.5)",
                background: "rgba(29, 78, 216, 0.11)",
        },
        draft: {
                label: "Pendente",
                description: "rascunho por fechar",
                Icon: CircleDashed,
                color: "var(--controversy)",
                border: "rgba(181, 80, 25, 0.52)",
                background: "rgba(181, 80, 25, 0.12)",
        },
        archived: {
                label: "Arquivado",
                description: "fora da fila ativa",
                Icon: Archive,
                color: "var(--ink-faint)",
                border: "rgba(104, 96, 85, 0.45)",
                background: "rgba(104, 96, 85, 0.12)",
        },
};

const editorialStatusMeta = (status) =>
        EDITORIAL_STATUS_META[status || "published"] || {
                label: status || "Publicado",
                description: "estado editorial",
                Icon: CircleDashed,
                color: "var(--ink-soft)",
                border: "var(--line-strong)",
                background: "rgba(104, 96, 85, 0.08)",
        };

const EditorialStatusBadge = ({ status }) => {
        const meta = editorialStatusMeta(status);
        const Icon = meta.Icon;

        return (
                <span
                        className="inline-flex min-h-7 items-center gap-1.5 border px-2 py-1 text-[10px] uppercase tracking-[0.16em]"
                        style={{
                                borderColor: meta.border,
                                backgroundColor: meta.background,
                                color: meta.color,
                        }}
                >
                        <Icon size={12} strokeWidth={1.7} />
                        {meta.label}
                </span>
        );
};

const RELEVANCE_LABELS = {
        portugal_direto: "Portugal direto",
        externo_relevante: "Externo relevante",
        manual_editorial: "Decisão manual",
        fora_de_escopo: "Fora de escopo",
};

const topicContextReviewSummary = (topic) => {
        if (!topic?.review_has_state) return null;
        return {
                findings: Array.from({ length: topic.review_findings_count || 0 }),
                cleared_excerpts: Array.from({ length: topic.review_cleared_count || 0 }),
                overall_assessment: topic.review_overall_assessment || "",
        };
};

export const EditorialQueue = ({
        editorialCounts,
        editing,
        hasMoreTopics,
        loadingTopics,
        onDelete,
        onEdit,
        onLoadMore,
        onTopicSearchChange,
        onTopicSearchClear,
        onTopicSearchSubmit,
        onToggleFeatured,
        statuses,
        topicSearch,
        topicStatusFilter,
        topics,
        totalTopics,
        visibleTopics,
        onStatusFilterChange,
}) => (
        <div>
                <div className="flex flex-col gap-3 mb-4">
                        <div className="overline text-[var(--ink-faint)]">
                                Fila editorial ({visibleTopics.length}/{topics.length} carregados de {totalTopics || topics.length})
                        </div>
                        <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
                                <form
                                        onSubmit={onTopicSearchSubmit}
                                        className="flex flex-1 min-w-0 overflow-hidden rounded-lg border border-[var(--line-strong)] bg-[var(--bg-deep)]"
                                >
                                        <div className="relative flex-1 min-w-0">
                                                <Search
                                                        size={14}
                                                        strokeWidth={1.5}
                                                        className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-faint)]"
                                                />
                                                <input
                                                        value={topicSearch}
                                                        onChange={(e) => onTopicSearchChange(e.target.value)}
                                                        placeholder="Procurar dossiê por título, resumo ou tag..."
                                                        className="w-full bg-transparent pl-9 pr-3 py-2 text-sm font-mono text-[var(--ink)] focus:outline-none"
                                                        aria-label="Procurar dossiês no editor"
                                                />
                                        </div>
                                        {topicSearch ? (
                                                <button
                                                        type="button"
                                                        onClick={onTopicSearchClear}
                                                        className="min-h-10 min-w-10 inline-flex items-center justify-center text-[var(--ink-soft)] hover:text-[var(--deception)]"
                                                        title="Limpar pesquisa"
                                                >
                                                        <X size={14} strokeWidth={1.5} />
                                                </button>
                                        ) : null}
                                        <button
                                                type="submit"
                                                disabled={loadingTopics}
                                                className="overline px-3 py-2 border-l border-[var(--line-strong)] text-[var(--ink)] hover:bg-[var(--ink)] hover:text-[var(--bg-deep)] disabled:opacity-50"
                                        >
                                                {loadingTopics ? "..." : "Pesquisar"}
                                        </button>
                                </form>

                                <label className="inline-flex items-center gap-2 text-[var(--ink-soft)]">
                                        <ListFilter size={14} strokeWidth={1.5} />
                                        <select
                                                value={topicStatusFilter}
                                                onChange={(e) => onStatusFilterChange(e.target.value)}
                                                className="border border-[var(--line-strong)] bg-[var(--bg-deep)] px-2 py-2 text-[10px] uppercase tracking-[0.18em] text-[var(--ink)]"
                                                aria-label="Filtrar fila editorial"
                                        >
                                                <option value="all">Todos ({topics.length})</option>
                                                {statuses.map((status) => (
                                                        <option key={status.value} value={status.value}>
                                                                {status.label} ({editorialCounts[status.value] || 0})
                                                        </option>
                                                ))}
                                        </select>
                                </label>
                        </div>
                        <div
                                className="grid grid-cols-2 lg:grid-cols-4 gap-2"
                                aria-label="Resumo por estado editorial"
                        >
                                {statuses.map((status) => {
                                        const meta = editorialStatusMeta(status.value);
                                        const Icon = meta.Icon;
                                        const active = topicStatusFilter === status.value;
                                        return (
                                                <button
                                                        key={status.value}
                                                        type="button"
                                                        onClick={() => onStatusFilterChange(status.value)}
                                                        className="min-h-14 border px-3 py-2 text-left transition-colors hover:bg-[var(--paper)]"
                                                        style={{
                                                                borderColor: active ? meta.border : "var(--line)",
                                                                backgroundColor: active ? meta.background : "transparent",
                                                        }}
                                                >
                                                        <div className="flex items-center justify-between gap-2">
                                                                <span
                                                                        className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em]"
                                                                        style={{ color: meta.color }}
                                                                >
                                                                        <Icon size={13} strokeWidth={1.7} />
                                                                        {meta.label}
                                                                </span>
                                                                <span className="font-mono text-sm text-[var(--ink)]">
                                                                        {editorialCounts[status.value] || 0}
                                                                </span>
                                                        </div>
                                                        <div className="mt-1 text-[11px] text-[var(--ink-faint)]">
                                                                {meta.description}
                                                        </div>
                                                </button>
                                        );
                                })}
                        </div>
                </div>
                <ul className="adm-card overflow-hidden">
                        {visibleTopics.map((topic) => {
                                const status = topic.editorial_status || "published";
                                const statusMeta = editorialStatusMeta(status);
                                const contextRiskReview = topicContextReviewSummary(topic);
                                const activeWarnings = activeWarningDetails(
                                        topic.quality_warnings,
                                        { contextRiskReview }
                                );
                                const technicalNotes = technicalWarningDetails(
                                        topic.quality_warnings,
                                        { contextRiskReview }
                                );
                                const warningCount = activeWarnings.length;
                                const warningPreview = activeWarnings.slice(0, 2);
                                return (
                                        <li
                                                key={topic.id}
                                                data-testid={ADMIN.topicListItem(topic.id)}
                                                className={`border-b border-[var(--line)] last:border-b-0 p-4 hover:bg-[var(--paper)] transition-colors ${
                                                        editing?.id === topic.id ? "bg-[var(--paper)]" : ""
                                                }`}
                                                style={{
                                                        borderLeft: `4px solid ${
                                                                editing?.id === topic.id
                                                                        ? "var(--controversy)"
                                                                        : statusMeta.border
                                                        }`,
                                                }}
                                        >
                                                <div className="flex items-start justify-between gap-3">
                                                        <button
                                                                type="button"
                                                                data-testid={ADMIN.editBtn(topic.id)}
                                                                onClick={() => onEdit(topic)}
                                                                className="text-left flex-1"
                                                        >
                                                                <div className="flex flex-wrap items-center gap-2">
                                                                        <EditorialStatusBadge status={status} />
                                                                        <span className="overline text-[var(--ink-soft)]">
                                                                                {topic.category} · {topic.controversy_score}/100
                                                                        </span>
                                                                        {topic.relevance_scope && topic.relevance_scope !== "portugal_direto" && (
                                                                                <span className="border border-[var(--line)] px-2 py-1 text-[10px] uppercase tracking-[0.14em] text-[var(--ink-soft)]">
                                                                                        {RELEVANCE_LABELS[topic.relevance_scope] || topic.relevance_scope}
                                                                                </span>
                                                                        )}
                                                                        {warningCount > 0 && (
                                                                                <span className="border border-[var(--line)] px-2 py-1 text-[10px] uppercase tracking-[0.14em] text-[var(--deception)]">
                                                                                        {warningCount} aviso{warningCount > 1 ? "s" : ""}
                                                                                </span>
                                                                        )}
                                                                        {warningCount === 0 && technicalNotes.length > 0 && (
                                                                                <span className="border border-[var(--line)] px-2 py-1 text-[10px] uppercase tracking-[0.14em] text-[var(--ink-soft)]">
                                                                                        {technicalNotes.length} nota{technicalNotes.length > 1 ? "s" : ""}
                                                                                </span>
                                                                        )}
                                                                </div>
                                                                <p className="font-serif text-lg leading-tight mt-2 text-[var(--ink)]">
                                                                        {topic.title}
                                                                </p>
                                                                {warningPreview.length > 0 && (
                                                                        <div className="mt-3 flex flex-wrap gap-2">
                                                                                {warningPreview.map((warning, index) => (
                                                                                        <span
                                                                                                key={`${topic.id}-${warning.raw}-${index}`}
                                                                                                className="inline-flex items-center border border-[var(--line)] bg-[var(--bg-deep)] px-2 py-1 font-mono text-[11px] text-[var(--ink-soft)]"
                                                                                        >
                                                                                                {warning.title}
                                                                                        </span>
                                                                                ))}
                                                                                {warningCount > warningPreview.length && (
                                                                                        <span className="inline-flex items-center border border-[var(--line)] bg-[var(--bg-deep)] px-2 py-1 font-mono text-[11px] text-[var(--ink-faint)]">
                                                                                                +{warningCount - warningPreview.length} aviso(s)
                                                                                        </span>
                                                                                )}
                                                                        </div>
                                                                )}
                                                        </button>
                                                        <div className="flex flex-col gap-2 mt-1">
                                                                <button
                                                                        type="button"
                                                                        onClick={() => onToggleFeatured(topic)}
                                                                        className={`min-h-8 min-w-8 inline-flex items-center justify-center transition-colors ${
                                                                                topic.is_featured
                                                                                        ? "text-yellow-500 hover:text-yellow-600"
                                                                                        : "text-[var(--ink-soft)] hover:text-yellow-500"
                                                                        }`}
                                                                        title="Destaque"
                                                                >
                                                                        <Star size={16} strokeWidth={1.5} fill={topic.is_featured ? "currentColor" : "none"} />
                                                                </button>
                                                                <button
                                                                        type="button"
                                                                        data-testid={ADMIN.deleteBtn(topic.id)}
                                                                        onClick={() => onDelete(topic.id)}
                                                                        className="min-h-8 min-w-8 inline-flex items-center justify-center text-[var(--ink-soft)] hover:text-[var(--deception)]"
                                                                        title="Eliminar"
                                                                >
                                                                        <Trash2 size={16} strokeWidth={1.5} />
                                                                </button>
                                                        </div>
                                                </div>
                                        </li>
                                );
                        })}
                        {visibleTopics.length === 0 && (
                                <li className="p-6 text-sm text-[var(--ink-faint)] italic">
                                        Sem dossiês neste estado.
                                </li>
                        )}
                </ul>
                {hasMoreTopics && (
                        <div className="mt-4 flex justify-center">
                                <button
                                        type="button"
                                        onClick={onLoadMore}
                                        disabled={loadingTopics}
                                        className="overline inline-flex items-center gap-2 border border-[var(--line-strong)] px-4 py-2 text-[var(--ink)] hover:bg-[var(--ink)] hover:text-[var(--bg-deep)] disabled:opacity-50"
                                >
                                        {loadingTopics ? (
                                                <Loader2 size={14} strokeWidth={1.5} className="animate-spin" />
                                        ) : (
                                                <ChevronDown size={14} strokeWidth={1.5} />
                                        )}
                                        Carregar mais dossiês
                                </button>
                        </div>
                )}
        </div>
);
